
import { GoogleGenAI, Type } from "@google/genai";
import { ReviewResponse } from '../types';

const getAiClient = () => {
  const apiKey = process.env.API_KEY || ''; 
  if (!apiKey) {
    console.warn("API_KEY is missing. AI features will fail.");
  }
  return new GoogleGenAI({ apiKey });
};

const SYSTEM_INSTRUCTION = `
You are "The Fellow Traveler," a wise, compassionate, and experienced 12-step sponsor. You are not a bot; you are a peer walking the road of happy destiny.

**Philosophy:** 
Strict adherence to the Big Book of Alcoholics Anonymous (4th Ed) and the Three Circles model.

**Your Task:**
Analyze the user's Step 11 Nightly Review. Provide deep, encouraging, and text-grounded feedback.

**Output Rules:**
1. **Insight (The Big Book):** Do not just mention a principle. Quote the Big Book or paraphrase it with a Page Number citation (e.g., "As it says on p.66..."). 
   - If they have resentment, quote page 66-67.
   - If they have fear, quote page 68.
   - If they were selfish, quote page 62.
   - Length: 2-3 sentences. Be warm but firm on the literature.
   
2. **Reflection (Support):** Validate their struggle. Use "We" language (e.g., "We often find that...", "I know how heavy that feels..."). Be the encouragement they need to keep trudging. Length: 2-3 sentences.

3. **Action:** A specific spiritual tool. (e.g., "Pray the Sick Man prayer," "Call a newcomer," "Read the acceptance paragraph on p.417").

4. **Risk Assessment:** 
   - Set 'isHighRisk' to TRUE if the user mentions: drinking, using, wanting to give up, self-harm, or extreme hopelessness.
   - This triggers a Sponsor Alert.

5. **Defect Scoring:** Analyze the text intensity for Resentment, Selfishness, Dishonesty, and Fear (0-10).
`;

export const analyzeNightlyReview = async (reviewText: string): Promise<ReviewResponse> => {
  const ai = getAiClient();
  
  try {
    const response = await ai.models.generateContent({
      model: "gemini-3-flash-preview",
      contents: `Analyze this nightly review Step 11 entry: "${reviewText}"`,
      config: {
        systemInstruction: SYSTEM_INSTRUCTION,
        responseMimeType: "application/json",
        responseSchema: {
          type: Type.OBJECT,
          properties: {
            insight: { type: Type.STRING, description: "Big Book wisdom with page number citations." },
            reflection: { type: Type.STRING, description: "Empathetic, supportive reflection." },
            action: { type: Type.STRING, description: "Specific spiritual action." },
            isHighRisk: { type: Type.BOOLEAN, description: "True if immediate sponsor intervention is needed." },
            sentimentScore: { type: Type.NUMBER, description: "1-10 Scale (1=Crisis, 10=Serene)." },
            defectScores: {
                type: Type.OBJECT,
                description: "Intensity of defects (0-10).",
                properties: {
                    resentment: { type: Type.NUMBER },
                    selfishness: { type: Type.NUMBER },
                    dishonesty: { type: Type.NUMBER },
                    fear: { type: Type.NUMBER }
                },
                required: ["resentment", "selfishness", "dishonesty", "fear"]
            }
          },
          required: ["insight", "reflection", "action", "isHighRisk", "sentimentScore", "defectScores"]
        }
      }
    });

    const jsonText = response.text || "{}";
    return JSON.parse(jsonText) as ReviewResponse;
  } catch (error) {
    console.error("Gemini Analysis Failed", error);
    return {
      insight: "We pause when agitated or doubtful. (p.87)",
      reflection: "I am having trouble connecting to the Reprieve Engine right now, but you are not alone. Keep breathing.",
      action: "Say the Serenity Prayer and try again later.",
      isHighRisk: false,
      sentimentScore: 5,
      defectScores: { resentment: 0, selfishness: 0, dishonesty: 0, fear: 0 }
    };
  }
};

export const checkGratitudeNudge = async (newItems: string[], history: string[]): Promise<string> => {
  const ai = getAiClient();
  
  const prompt = `
    User's new gratitude items: ${JSON.stringify(newItems)}
    User's recent history: ${JSON.stringify(history)}
    
    Task: 
    1. Compare new items against history.
    2. If repetitive, gently nudge them to be more specific (e.g., "I see you're grateful for your dog again... what specifically today?").
    3. If unique, offer a brief encouraging affirmation.
    4. Keep it under 2 sentences.
  `;

  try {
    const response = await ai.models.generateContent({
      model: "gemini-3-flash-preview",
      contents: prompt,
      config: {
        systemInstruction: SYSTEM_INSTRUCTION
      }
    });
    return response.text || "Keep up the gratitude practice!";
  } catch (error) {
    return "Great job focusing on the positive today.";
  }
};
